<template>
  <b-modal
    v-model="showModal"
    :title="`Biometric Information Consent — ${displayName || userName}`"
    size="lg"
    :busy="busy"
    :ok-disabled="!canSubmit"
    ok-title="Confirm Consent"
    cancel-title="Cancel"
    ok-variant="primary"
    no-fade
    @ok.prevent="onConfirm"
    @cancel="onCancel"
    @hidden="onHidden"
  >
    <div v-if="!policyConfigured" class="text-warning">
      The biometric privacy policy has not been configured for this tenant. Upload a disclosure PDF in
      <strong>Configuration → Compliance → Biometric Privacy Disclosure PDF</strong> before enrolling
      fingerprints.
    </div>
    <template v-else>
      <p class="text-muted small mb-2">
        Policy version: <span class="pi-policy-version">{{ shortVersion }}</span>
      </p>

      <div v-if="loadingDoc" class="text-center my-4">
        <b-spinner small class="mr-2" /> Loading disclosure…
      </div>
      <div v-else-if="docError" class="alert alert-danger">{{ docError }}</div>
      <iframe
        v-else-if="documentUrl"
        :src="documentUrl"
        class="pi-consent-pdf"
        title="Biometric privacy disclosure"></iframe>

      <b-form-checkbox v-model="acknowledged" class="mt-3" :disabled="!documentUrl">
        I, <strong>{{ displayName || userName }}</strong>, have read this disclosure and consent to the
        collection, storage, and use of my fingerprint template as described above.
      </b-form-checkbox>

      <b-alert :show="!!error" variant="danger" class="mt-3 mb-0">{{ error }}</b-alert>
    </template>
  </b-modal>
</template>

<script lang="ts">
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import PositiveIdService, { BiometricConsentRecord } from '@/services/PositiveIdService';

@Component({ name: 'BiometricConsentDialog' })
export default class BiometricConsentDialog extends Vue {
  @Prop({ type: Boolean, default: false }) readonly show!: boolean;
  @Prop({ type: String, required: true }) readonly userName!: string;
  @Prop({ type: String, default: '' }) readonly displayName!: string;
  @Prop({ type: String, default: '' }) readonly policyVersion!: string;
  @Prop({ type: Number, default: null }) readonly documentId!: number | null;

  private showModal = false;
  private acknowledged = false;
  private busy = false;
  private error = '';
  private docError = '';
  private loadingDoc = false;
  private documentUrl: string | null = null;

  private get policyConfigured(): boolean {
    return !!this.documentId && !!this.policyVersion;
  }

  /** Short, displayable form of the SHA-256 hash that acts as the policy version. */
  private get shortVersion(): string {
    return this.policyVersion ? this.policyVersion.substring(0, 8).toLowerCase() : '';
  }

  private get canSubmit(): boolean {
    return !this.busy && !this.loadingDoc && this.acknowledged && this.policyConfigured && !!this.documentUrl;
  }

  @Watch('show', { immediate: true })
  async onShowChanged(value: boolean): Promise<void> {
    this.showModal = value;
    if (value) {
      this.acknowledged = false;
      this.error = '';
      this.docError = '';
      await this.loadDocument();
    }
  }

  @Watch('showModal')
  onShowModalChanged(value: boolean): void {
    if (!value && value !== this.show) this.$emit('update:show', false);
  }

  @Watch('documentId')
  async onDocumentIdChanged(): Promise<void> {
    if (this.showModal) await this.loadDocument();
  }

  private async loadDocument(): Promise<void> {
    this.revokeDocumentUrl();
    if (!this.documentId) return;
    this.loadingDoc = true;
    this.docError = '';
    try {
      const blob = await PositiveIdService.getBiometricConsentDocumentBlob(this.documentId);
      this.documentUrl = window.URL.createObjectURL(blob);
    } catch {
      this.docError = 'Failed to load the disclosure document.';
    } finally {
      this.loadingDoc = false;
    }
  }

  private revokeDocumentUrl(): void {
    if (this.documentUrl) {
      try { window.URL.revokeObjectURL(this.documentUrl); } catch { /* ignore */ }
      this.documentUrl = null;
    }
  }

  async onConfirm(): Promise<void> {
    if (!this.canSubmit) return;
    this.busy = true;
    this.error = '';
    try {
      const record: BiometricConsentRecord = await PositiveIdService.recordBiometricConsent(
        this.userName,
        this.policyVersion,
      );
      this.$emit('consented', record);
      this.showModal = false;
    } catch (err: any) {
      this.error = err?.response?.data ?? 'Failed to record consent. Please try again.';
    } finally {
      this.busy = false;
    }
  }

  onCancel(): void {
    this.$emit('cancelled');
  }

  onHidden(): void {
    this.revokeDocumentUrl();
  }

  beforeDestroy(): void {
    this.revokeDocumentUrl();
  }
}
</script>

<style scoped>
.pi-consent-pdf {
  width: 100%;
  height: 480px;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  background: #f8f9fa;
}
.pi-policy-version {
  font-family: monospace;
  font-size: 0.85rem;
}
</style>
